import React, { useState, useEffect, useMemo } from 'react';
import { Phone, MessageSquare, StickyNote, LayoutGrid, List, Trash2, RefreshCw, Sparkles, Plus, ChevronDown, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AssignSelect from '../components/AssignSelect';
import CreateStudentProfile from './CreateStudentProfile';
import { fmtDateTime } from '../lib/dateFmt';

const PIPELINE_STAGES = [
  { key: 'new', label: 'New', color: 'bg-slate-100 text-slate-800 border-slate-300' },
  { key: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { key: 'qualified', label: 'Qualified', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  { key: 'converted', label: 'Converted', color: 'bg-green-100 text-green-800 border-green-300' },
  { key: 'incomplete', label: 'Incomplete Docs', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { key: 'lost', label: 'Lost', color: 'bg-rose-100 text-rose-800 border-rose-300' },
];

const API = process.env.REACT_APP_BACKEND_URL;

const sourceBadge = (source) => {
  const map = {
    meta: 'bg-indigo-100 text-indigo-700',
    google: 'bg-emerald-100 text-emerald-700',
    website: 'bg-brand-coral/15 text-brand-coral-deep',
    manual: 'bg-gray-100 text-gray-700',
  };
  return map[source] || 'bg-gray-100 text-gray-700';
};

const HEAT_META = {
  hot: { label: '🔥 HOT', color: 'bg-rose-100 text-rose-700 border-rose-200', bar: 'bg-rose-500' },
  warm: { label: '☀ Warm', color: 'bg-amber-100 text-amber-700 border-amber-200', bar: 'bg-amber-500' },
  cold: { label: '❄ Cold', color: 'bg-sky-100 text-sky-700 border-sky-200', bar: 'bg-sky-500' },
};

const heatColor = (heat) => HEAT_META[heat] || { label: heat, color: 'bg-gray-100 text-gray-700 border-gray-200', bar: 'bg-gray-400' };

export default function LeadManagement() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [me, setMe] = useState(null);
  const [bulkSelection, setBulkSelection] = useState(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [counsellors, setCounsellors] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  // Convert-to-student dialog state
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertLead, setConvertLead] = useState(null);

  function openConvert(lead) {
    setConvertLead(lead);
    setConvertOpen(true);
  }

  function onConvertSuccess() {
    setConvertOpen(false);
    setConvertLead(null);
    // Refresh so the lead row shows the new "Student created" badge.
    fetchLeads();
    fetchActivityCounts();
  }

  useEffect(() => {
    fetch(`${API}/api/staff/me`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setMe(d))
      .catch(() => {});
  }, []);
  const isAdmin = me?.role === 'admin';
  const canAssign = isAdmin || me?.role === 'manager';

  // Fetch counsellors for the bulk-assign dropdown (admins/managers only).
  useEffect(() => {
    if (!canAssign) return;
    fetch(`${API}/api/admin/counsellors`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : { counsellors: [] })
      .then((d) => setCounsellors(d.counsellors || []))
      .catch(() => {});
  }, [canAssign]);

  function toggleSelect(id, e) {
    if (e) e.stopPropagation();
    setBulkSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function bulkAssignTo(userId) {
    setBulkSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/leads/bulk-assign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: Array.from(bulkSelection), assigned_to: userId }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Bulk assign failed');
      setBulkSelection(new Set());
      setBulkAssignOpen(false);
      fetchLeads();
    } catch (err) {
      alert(err.message);
    } finally {
      setBulkSaving(false);
    }
  }
  const [createOpen, setCreateOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [bulkRescoring, setBulkRescoring] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', phone: '', email: '', program: '', country: '', notes: '', recruiter_id: '' });
  const [recruiterOptions, setRecruiterOptions] = useState([]);

  useEffect(() => {
    if (!createOpen || recruiterOptions.length > 0) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/recruiters`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const rows = data.recruiters || data;
        setRecruiterOptions(
          Array.isArray(rows)
            ? rows.filter((r) => r.recruiter_id).map((r) => ({
                id: r.recruiter_id,
                label: r.company_name || r.contact_name || r.recruiter_id,
              }))
            : []
        );
      } catch (_) { /* offline */ }
    })();
  }, [createOpen, recruiterOptions.length]);
  const [creating, setCreating] = useState(false);
  const [smsBody, setSmsBody] = useState('');

  // ====== Layout + activity tracking state ======
  const [view, setView] = useState(() => localStorage.getItem('leads_view') || 'table'); // 'table' | 'board'
  const [stageFilter, setStageFilter] = useState('all'); // table-view stage filter
  const [search, setSearch] = useState('');
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityForm, setActivityForm] = useState({ leadId: null, leadName: '', type: 'call', outcome: '', note: '' });
  const [savingActivity, setSavingActivity] = useState(false);
  const [leadActivities, setLeadActivities] = useState([]); // for detail dialog
  const [activityCounts, setActivityCounts] = useState({}); // {lead_id: {call, sms, ...}}

  useEffect(() => { localStorage.setItem('leads_view', view); }, [view]);

  // Load activity counts in bulk so we can show badges on each lead row
  const fetchActivityCounts = async () => {
    try {
      const r = await fetch(`${API}/api/admin/lead-activities?limit=1000`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      const map = {};
      for (const a of (d.activities || [])) {
        const id = a.lead_id;
        if (!map[id]) map[id] = { call: 0, sms: 0, whatsapp: 0, email: 0, note: 0, meeting: 0, last_at: null, last_actor: '' };
        if (map[id][a.type] != null) map[id][a.type]++;
        if (!map[id].last_at || a.created_at > map[id].last_at) {
          map[id].last_at = a.created_at;
          map[id].last_actor = a.actor_name || '';
        }
      }
      setActivityCounts(map);
    } catch {/* ignore */}
  };

  function openActivity(leadId, leadName, type) {
    setActivityForm({ leadId, leadName: leadName || 'Unnamed lead', type, outcome: '', note: '' });
    setActivityOpen(true);
  }

  async function saveActivity() {
    setSavingActivity(true);
    try {
      const r = await fetch(`${API}/api/admin/leads/${activityForm.leadId}/activities`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: activityForm.type,
          outcome: activityForm.outcome || null,
          note: activityForm.note,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to log activity');
      }
      setActivityOpen(false);
      await fetchActivityCounts();
      if (selected?.id === activityForm.leadId) {
        loadLeadActivities(activityForm.leadId);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingActivity(false);
    }
  }

  async function loadLeadActivities(leadId) {
    try {
      const r = await fetch(`${API}/api/admin/leads/${leadId}/activities`, { credentials: 'include' });
      if (!r.ok) { setLeadActivities([]); return; }
      const d = await r.json();
      setLeadActivities(d.activities || []);
    } catch {
      setLeadActivities([]);
    }
  }

  const fetchLeads = async () => {
    try {
      const res = await fetch(`${API}/api/admin/leads`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load leads');
      const data = await res.json();
      setLeads(data.leads || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchActivityCounts();
  }, []);

  const stages = useMemo(() => {
    const grouped = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.key, []]));
    for (const l of leads) grouped[l.status]?.push(l);
    // Newest first inside every stage column.
    Object.values(grouped).forEach((arr) =>
      arr.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    );
    return grouped;
  }, [leads]);

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads
      .filter((l) => stageFilter === 'all' || l.status === stageFilter)
      .filter((l) => {
        if (!q) return true;
        return (
          (l.name || '').toLowerCase().includes(q) ||
          (l.phone || '').toLowerCase().includes(q) ||
          (l.email || '').toLowerCase().includes(q) ||
          (l.program_interest || '').toLowerCase().includes(q) ||
          (l.country_interest || '').toLowerCase().includes(q)
        );
      })
      // Newest lead always on top — admins want to see fresh ad submissions
      // immediately. AI score is still visible in the AI Heat column.
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [leads, stageFilter, search]);

  const openDetails = async (leadId) => {
    try {
      const res = await fetch(`${API}/api/admin/leads/${leadId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load lead');
      const data = await res.json();
      setSelected(data);
      setDetailOpen(true);
      loadLeadActivities(leadId);
    } catch (e) {
      alert(e.message);
    }
  };

  const updateStatus = async (leadId, status) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/leads/${leadId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Update failed');
      await fetchLeads();
      if (selected?.id === leadId) {
        const updated = await res.json();
        setSelected(updated);
      }
    } catch (e) {
      alert(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const rescoreLead = async (leadId) => {
    if (rescoring) return;
    setRescoring(true);
    try {
      const res = await fetch(`${API}/api/admin/leads/${leadId}/rescore`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || `Failed (HTTP ${res.status})`);
        return;
      }
      // Patch the selected card + leads list with the fresh score
      const patch = {
        ai_score: data.score,
        ai_heat: data.heat,
        ai_tags: data.tags,
        ai_reasoning: data.reasoning,
        ai_score_method: data.method,
        ai_scored_at: data.scored_at,
      };
      setSelected((cur) => cur ? { ...cur, ...patch } : cur);
      setLeads((cur) => cur.map((l) => l.id === leadId ? { ...l, ...patch } : l));
    } catch (err) {
      alert(err.message);
    } finally {
      setRescoring(false);
    }
  };

  const triggerFollowUp = async (leadId) => {
    if (!window.confirm('Re-trigger SMS + WhatsApp + AI voice call for this lead?')) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/leads/${leadId}/trigger-followup`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Trigger failed');
      alert(`Follow-up dispatched.\nSMS: ${data.results.sms.status}\nWhatsApp: ${data.results.whatsapp.status}\nVoice: ${data.results.voice.status}`);
      await openDetails(leadId);
      fetchLeads();
    } catch (e) {
      alert(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const sendSms = async (leadId) => {
    if (!smsBody.trim()) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/leads/${leadId}/send-sms`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: smsBody }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      alert(`SMS status: ${data.result.status}`);
      setSmsBody('');
      await openDetails(leadId);
    } catch (e) {
      alert(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const createLead = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/admin/leads`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...createForm,
          source: 'manual',
          auto_follow_up: false,
          recruiter_id: createForm.recruiter_id || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setCreateOpen(false);
      setCreateForm({ name: '', phone: '', email: '', program: '', country: '', notes: '', recruiter_id: '' });
      fetchLeads();
      alert(`Lead created. ID: ${data.id}`);
    } catch (err) {
      alert(err.message);
    } finally {
      setCreating(false);
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      const res = await fetch(`${API}/api/admin/leads/${leadId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Delete failed');
      setDetailOpen(false);
      fetchLeads();
    } catch (e) {
      alert(e.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="leads-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-tge-navy" />
      </div>
    );
  }

  return (
    <div data-testid="lead-management">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif-display text-3xl text-tge-navy">Lead Management</h2>
          <p className="text-sm text-tge-muted mt-1">
            Capture, nurture & convert Meta / Google Ads leads with automated SMS, WhatsApp & AI voice follow-ups.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchLeads} data-testid="lead-refresh-btn" className="rounded-full">
            Refresh
          </Button>
          {(isAdmin || me?.role === 'manager') && (
            <Button
              variant="outline"
              className="rounded-full text-tge-navy/80"
              data-testid="lead-rescore-all-btn"
              disabled={bulkRescoring}
              onClick={async () => {
                if (!window.confirm('Re-score every visible lead with AI? This may take a minute and will use LLM credits.')) return;
                setBulkRescoring(true);
                try {
                  const res = await fetch(`${API}/api/admin/leads/rescore-all`, { method: 'POST', credentials: 'include' });
                  const d = await res.json();
                  if (!res.ok) { alert(d.detail || 'Failed'); return; }
                  alert(`AI re-scored ${d.scored} of ${d.total} leads (${d.failed} failed).`);
                  fetchLeads();
                } catch (e) { alert(e.message); } finally { setBulkRescoring(false); }
              }}
            >
              {bulkRescoring ? '⏳ Scoring all…' : '✨ AI score all'}
            </Button>
          )}
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-tge-navy hover:bg-tge-navy-deep text-white rounded-full" data-testid="lead-create-btn">
                + New Lead
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Lead Manually</DialogTitle>
                <DialogDescription>Lead will receive automatic SMS, WhatsApp & AI voice call.</DialogDescription>
              </DialogHeader>
              <form onSubmit={createLead} className="space-y-3">
                <div>
                  <Label>Name *</Label>
                  <Input required value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} data-testid="lead-form-name" />
                </div>
                <div>
                  <Label>Phone (E.164, e.g. +15551234567) *</Label>
                  <Input required value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} data-testid="lead-form-phone" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} data-testid="lead-form-email" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Program Interest</Label>
                    <Input value={createForm.program} onChange={(e) => setCreateForm({ ...createForm, program: e.target.value })} data-testid="lead-form-program" />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input value={createForm.country} onChange={(e) => setCreateForm({ ...createForm, country: e.target.value })} data-testid="lead-form-country" />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} data-testid="lead-form-notes" />
                </div>
                <div>
                  <Label>Attribute to recruiter (optional)</Label>
                  <select
                    value={createForm.recruiter_id}
                    onChange={(e) => setCreateForm({ ...createForm, recruiter_id: e.target.value })}
                    className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm"
                    data-testid="lead-form-recruiter"
                  >
                    <option value="">Direct — no recruiter</option>
                    {recruiterOptions.map((r) => (
                      <option key={r.id} value={r.id}>{r.label} ({r.id})</option>
                    ))}
                  </select>
                  <div className="text-[11px] text-tge-navy/50 mt-1">
                    Use this if you&apos;re entering the lead on behalf of a recruiter — it links the lead + future student profile to their portfolio.
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={creating} data-testid="lead-form-submit">
                    {creating ? 'Creating…' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Bulk action bar (sticky) — visible when leads are selected */}
      {canAssign && bulkSelection.size > 0 && (
        <div className="sticky top-3 z-20 mb-4 bg-tge-navy text-white rounded-2xl shadow-lg shadow-tge-navy/30 px-5 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="bulk-action-bar">
          <div className="flex items-center gap-3">
            <span className="bg-tge-gold/20 text-tge-gold px-2.5 py-1 rounded-full text-sm font-semibold">{bulkSelection.size}</span>
            <span className="text-sm">leads selected</span>
          </div>
          <div className="flex items-center gap-2 relative">
            <button
              onClick={() => setBulkAssignOpen((o) => !o)}
              disabled={bulkSaving}
              className="bg-tge-gold hover:bg-tge-gold/90 text-tge-navy font-semibold px-4 py-1.5 rounded-full text-sm transition disabled:opacity-50"
              data-testid="bulk-assign-trigger"
            >
              {bulkSaving ? 'Saving…' : 'Assign to…'}
            </button>
            <button
              onClick={() => setBulkSelection(new Set())}
              className="text-xs text-white/70 hover:text-white transition px-3 py-1.5 rounded-full border border-white/20 hover:border-white/40"
              data-testid="bulk-clear-btn"
            >
              Clear
            </button>
            {bulkAssignOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setBulkAssignOpen(false)} />
                <div className="absolute z-40 right-0 top-11 w-64 bg-white text-tge-navy border border-tge-cream rounded-xl shadow-xl overflow-hidden" data-testid="bulk-assign-dropdown">
                  <div className="px-3 py-2 border-b border-tge-cream text-[10px] uppercase tracking-wider text-tge-muted font-semibold">
                    Assign {bulkSelection.size} leads to
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    <button
                      onClick={() => bulkAssignTo(null)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-tge-cream-soft text-tge-muted"
                    >
                      Unassigned
                    </button>
                    {counsellors.length === 0 && (
                      <div className="px-3 py-3 text-[11px] text-tge-muted italic">No counsellors found.</div>
                    )}
                    {counsellors.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => bulkAssignTo(c.id)}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-tge-cream-soft"
                      >
                        <span className="block text-tge-navy font-medium">{c.name}</span>
                        <span className="block text-[10px] text-tge-muted">{c.email} · {c.role}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Webhook info — admin only */}
      {isAdmin && (
        <Card className="mb-6 border-dashed border-blue-300 bg-blue-50/60">
          <CardContent className="py-4 text-xs text-gray-700">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <strong>Meta Ads Webhook:</strong>
                <code className="block bg-white px-2 py-1 mt-1 rounded border text-[11px] break-all">
                  POST {API}/api/webhooks/meta-leads
                </code>
              </div>
              <div>
                <strong>Google Ads Webhook:</strong>
                <code className="block bg-white px-2 py-1 mt-1 rounded border text-[11px] break-all">
                  POST {API}/api/webhooks/google-leads
                </code>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded">{error}</div>}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {PIPELINE_STAGES.map((s) => (
          <Card key={s.key} data-testid={`lead-stat-${s.key}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-500 uppercase">{s.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stages[s.key]?.length || 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* View toolbar: search + stage chips + view toggle */}
      <div className="bg-white border border-tge-navy/5 rounded-2xl p-3 mb-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid="leads-toolbar">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, phone, email, program…"
          className="lg:max-w-sm"
          data-testid="leads-search"
        />
        <div className="flex flex-wrap items-center gap-1.5 flex-1">
          {[{ key: 'all', label: `All (${leads.length})` }, ...PIPELINE_STAGES.map((s) => ({ key: s.key, label: `${s.label} (${stages[s.key]?.length || 0})` }))].map((chip) => {
            const isActive = stageFilter === chip.key;
            return (
              <button
                key={chip.key}
                onClick={() => setStageFilter(chip.key)}
                data-testid={`stage-chip-${chip.key}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${isActive ? 'bg-tge-navy text-white border-tge-navy' : 'bg-white text-tge-navy/80 border-tge-navy/10 hover:border-tge-navy/30'}`}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
        <div className="inline-flex bg-tge-cream-soft rounded-full p-1 self-start lg:self-auto">
          <button
            onClick={() => setView('table')}
            data-testid="view-table-btn"
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${view === 'table' ? 'bg-white text-tge-navy shadow-sm' : 'text-tge-navy/60 hover:text-tge-navy'}`}
          >
            <List size={13} /> Table
          </button>
          <button
            onClick={() => setView('board')}
            data-testid="view-board-btn"
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${view === 'board' ? 'bg-white text-tge-navy shadow-sm' : 'text-tge-navy/60 hover:text-tge-navy'}`}
          >
            <LayoutGrid size={13} /> Board
          </button>
        </div>
      </div>

      {view === 'table' && (
        <LeadTable
          leads={filteredLeads}
          activityCounts={activityCounts}
          canSelect={canAssign}
          selection={bulkSelection}
          toggleSelect={toggleSelect}
          openDetails={openDetails}
          openActivity={openActivity}
          openConvert={openConvert}
        />
      )}

      {view === 'board' && (
        <LeadBoard
          stages={stages}
          activityCounts={activityCounts}
          canSelect={canAssign}
          selection={bulkSelection}
          toggleSelect={toggleSelect}
          openDetails={openDetails}
          openActivity={openActivity}
          openConvert={openConvert}
        />
      )}

      {/* Detail drawer */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selected.name || 'Unnamed lead'}
                  <Badge className={`${sourceBadge(selected.source)} border-0`}>{selected.source}</Badge>
                </DialogTitle>
                <DialogDescription>
                  Created {fmtDateTime(selected.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Phone:</span> {selected.phone || '—'}</div>
                <div><span className="text-gray-500">Email:</span> {selected.email || '—'}</div>
                <div><span className="text-gray-500">Program:</span> {selected.program_interest || '—'}</div>
                <div><span className="text-gray-500">Country:</span> {selected.country_interest || '—'}</div>
              </div>

              {selected.notes && (
                <div className="text-sm">
                  <div className="text-gray-500 mb-1">Notes</div>
                  <div className="bg-gray-50 border rounded p-2">{selected.notes}</div>
                </div>
              )}

              {/* AI score + tags */}
              <div className="bg-gradient-to-br from-tge-cream-soft to-white border border-tge-navy/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider text-tge-navy/60 font-semibold flex items-center gap-1.5">
                    ✨ AI Lead Score
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[11px] h-7 gap-1"
                    onClick={() => rescoreLead(selected.id)}
                    disabled={rescoring}
                    data-testid="lead-rescore-btn"
                  >
                    {rescoring ? '⏳ Scoring…' : '↻ Re-score'}
                  </Button>
                </div>
                {selected.ai_score != null ? (
                  <>
                    <div className="flex items-baseline gap-3 mb-2">
                      <div className={`font-serif-display text-4xl ${selected.ai_heat === 'hot' ? 'text-rose-600' : selected.ai_heat === 'warm' ? 'text-amber-600' : 'text-sky-600'}`}>
                        {selected.ai_score}
                      </div>
                      <Badge className={`${heatColor(selected.ai_heat).color} border`}>
                        {heatColor(selected.ai_heat).label}
                      </Badge>
                      {selected.ai_score_method && (
                        <span className="text-[10px] uppercase tracking-wider text-tge-navy/40">
                          {selected.ai_score_method === 'llm' ? 'AI' : selected.ai_score_method.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                      <div
                        className={`h-full ${heatColor(selected.ai_heat).bar} transition-all`}
                        style={{ width: `${selected.ai_score}%` }}
                      />
                    </div>
                    {selected.ai_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {selected.ai_tags.map((t) => (
                          <Badge key={t} className="bg-white border border-tge-navy/15 text-tge-navy/80 text-[10px] font-mono">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {selected.ai_reasoning && (
                      <div className="text-xs text-tge-navy/70 italic bg-white/60 rounded p-2 border border-tge-navy/5">
                        {selected.ai_reasoning}
                      </div>
                    )}
                    {selected.ai_scored_at && (
                      <div className="text-[10px] text-tge-navy/40 mt-2">
                        Scored {fmtDateTime(selected.ai_scored_at)}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-tge-navy/60 italic">
                    Not scored yet. Click <span className="font-semibold">Re-score</span> to run AI analysis.
                  </div>
                )}
              </div>

              {/* Stage + Assignee selectors */}
              <div className="flex items-center gap-3 flex-wrap">
                <Label className="text-sm">Stage:</Label>
                <Select
                  value={selected.status}
                  onValueChange={(v) => updateStatus(selected.id, v)}
                >
                  <SelectTrigger className="w-48" data-testid="lead-stage-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PIPELINE_STAGES
                      // Once converted (or later flagged incomplete) the
                      // only allowed onward moves are between those two —
                      // keeps attribution intact but lets staff flag
                      // missing paperwork.
                      .filter((s) =>
                        selected.status === 'converted' || selected.status === 'incomplete'
                          ? s.key === 'converted' || s.key === 'incomplete'
                          : true
                      )
                      .map((s) => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {selected.status === 'converted' && (
                  <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="lead-stage-locked">
                    Converted — only &quot;Incomplete Docs&quot; allowed onward
                  </span>
                )}
                {selected.status === 'incomplete' && (
                  <span className="text-[11px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5">
                    Documents incomplete
                  </span>
                )}
                {(isAdmin || me?.role === 'manager') && (
                  <>
                    <Label className="text-sm ml-2">Assigned to:</Label>
                    <AssignSelect
                      resourceType="lead"
                      resourceId={selected.id}
                      currentAssigneeId={selected.assigned_to}
                      onAssigned={({ assigned_to }) => {
                        setSelected({ ...selected, assigned_to });
                        fetchLeads();
                      }}
                    />
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button
                  size="sm"
                  onClick={() => triggerFollowUp(selected.id)}
                  disabled={actionLoading}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="lead-trigger-followup-btn"
                >
                  🚀 Trigger SMS + WA + Voice
                </Button>
                <Button size="sm" variant="destructive" onClick={() => deleteLead(selected.id)} data-testid="lead-delete-btn">
                  Delete
                </Button>
              </div>

              {/* Quick SMS */}
              <div className="border-t pt-3">
                <Label className="text-sm">Send Custom SMS</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={smsBody} onChange={(e) => setSmsBody(e.target.value)} placeholder="Your message..." data-testid="lead-sms-input" />
                  <Button onClick={() => sendSms(selected.id)} disabled={actionLoading || !smsBody.trim()} data-testid="lead-sms-send-btn">
                    Send
                  </Button>
                </div>
              </div>

              {/* Quick actions: manual sales activity */}
              <div className="bg-tge-cream-soft border border-tge-navy/5 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wider text-tge-navy/60 font-semibold mb-3">Log a sales action</div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => openActivity(selected.id, selected.name, 'call')}
                    data-testid="quick-action-call"
                    className="flex flex-col items-center gap-1 bg-white border border-tge-navy/10 hover:border-emerald-400 hover:bg-emerald-50 rounded-xl py-3 transition"
                  >
                    <Phone size={18} className="text-emerald-600" />
                    <span className="text-xs font-semibold text-tge-navy">Called</span>
                  </button>
                  <button
                    onClick={() => openActivity(selected.id, selected.name, 'sms')}
                    data-testid="quick-action-sms"
                    className="flex flex-col items-center gap-1 bg-white border border-tge-navy/10 hover:border-sky-400 hover:bg-sky-50 rounded-xl py-3 transition"
                  >
                    <MessageSquare size={18} className="text-sky-600" />
                    <span className="text-xs font-semibold text-tge-navy">Texted</span>
                  </button>
                  <button
                    onClick={() => openActivity(selected.id, selected.name, 'note')}
                    data-testid="quick-action-note"
                    className="flex flex-col items-center gap-1 bg-white border border-tge-navy/10 hover:border-violet-400 hover:bg-violet-50 rounded-xl py-3 transition"
                  >
                    <StickyNote size={18} className="text-violet-600" />
                    <span className="text-xs font-semibold text-tge-navy">Note</span>
                  </button>
                </div>
                {selected.status === 'converted' && !selected.converted_to_student_id && (
                  <button
                    onClick={() => { setDetailOpen(false); openConvert(selected); }}
                    data-testid="quick-action-convert"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm rounded-xl py-2.5 transition shadow-sm"
                  >
                    <UserPlus size={15} />
                    Convert this lead into a student application
                  </button>
                )}
                {selected.converted_to_student_id && (
                  <div className="mt-3 text-center text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl py-2">
                    ✓ Student profile created — ID {selected.converted_to_student_id}
                  </div>
                )}
              </div>

              {/* Logs */}
              <div className="border-t pt-3 space-y-3">
                <div className="text-sm font-semibold">Activity Log</div>
                <ManualActivityList entries={leadActivities} />
                <LogSection title="📱 Auto SMS" entries={selected.sms_log} />
                <LogSection title="💬 Auto WhatsApp" entries={selected.whatsapp_log} />
                <LogSection title="📞 Auto Voice Call" entries={selected.voice_log} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick-action activity dialog */}
      <Dialog open={activityOpen} onOpenChange={setActivityOpen}>
        <DialogContent className="max-w-md" data-testid="activity-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activityForm.type === 'call' && <Phone size={18} className="text-emerald-600" />}
              {activityForm.type === 'sms' && <MessageSquare size={18} className="text-sky-600" />}
              {activityForm.type === 'whatsapp' && <MessageSquare size={18} className="text-emerald-600" />}
              {activityForm.type === 'note' && <StickyNote size={18} className="text-violet-600" />}
              Log {activityForm.type === 'call' ? 'call' : activityForm.type === 'sms' ? 'text message' : activityForm.type === 'whatsapp' ? 'WhatsApp' : 'note'}
            </DialogTitle>
            <DialogDescription>
              Lead: <span className="font-medium text-tge-navy">{activityForm.leadName}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select value={activityForm.type} onValueChange={(v) => setActivityForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">📞 Call</SelectItem>
                  <SelectItem value="sms">💬 Text / SMS</SelectItem>
                  <SelectItem value="whatsapp">💬 WhatsApp</SelectItem>
                  <SelectItem value="email">✉️ Email</SelectItem>
                  <SelectItem value="meeting">👥 Meeting</SelectItem>
                  <SelectItem value="note">📝 Note (no outreach)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {activityForm.type !== 'note' && (
              <div>
                <Label>Outcome (optional)</Label>
                <Select value={activityForm.outcome || ''} onValueChange={(v) => setActivityForm((f) => ({ ...f, outcome: v }))}>
                  <SelectTrigger data-testid="activity-outcome-select">
                    <SelectValue placeholder="Pick outcome…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reached">✅ Reached / Spoke</SelectItem>
                    <SelectItem value="no_answer">📵 No answer</SelectItem>
                    <SelectItem value="voicemail">📥 Left voicemail</SelectItem>
                    <SelectItem value="wrong_number">❌ Wrong number</SelectItem>
                    <SelectItem value="callback_requested">↩️ Callback requested</SelectItem>
                    <SelectItem value="not_interested">🚫 Not interested</SelectItem>
                    <SelectItem value="interested">👍 Interested</SelectItem>
                    <SelectItem value="qualified">🎯 Qualified</SelectItem>
                    <SelectItem value="converted">🏆 Converted</SelectItem>
                    <SelectItem value="lost">💔 Lost</SelectItem>
                    <SelectItem value="other">— Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Note (optional)</Label>
              <Textarea
                value={activityForm.note}
                onChange={(e) => setActivityForm((f) => ({ ...f, note: e.target.value }))}
                placeholder={activityForm.type === 'note' ? 'What did you learn about this lead?' : 'What did you talk about? Any next steps?'}
                rows={4}
                data-testid="activity-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivityOpen(false)}>Cancel</Button>
            <Button onClick={saveActivity} disabled={savingActivity} className="bg-tge-navy hover:bg-tge-navy-deep" data-testid="activity-save-btn">
              {savingActivity ? 'Saving…' : 'Log activity'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert-to-student dialog */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0" data-testid="convert-lead-dialog">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="flex items-center gap-2 text-tge-navy">
              <UserPlus size={20} className="text-amber-600" />
              Convert lead to student application
            </DialogTitle>
            <DialogDescription>
              The form below is pre-filled from <strong>{convertLead?.name || 'this lead'}</strong>.
              Recruiter is auto-set to <em>Direct social leads</em> — change it if a recruiter referred them.
            </DialogDescription>
          </DialogHeader>
          {convertLead && (
            <div className="px-6 pb-6">
              <CreateStudentProfile
                adminMode={true}
                prefill={{
                  student_name: convertLead.name || '',
                  email: convertLead.email || '',
                  phone: convertLead.phone || '',
                  dob: convertLead.dob || '',
                }}
                prefillRecruiterId="DIRECT_SOCIAL_LEADS"
                sourceLeadId={convertLead.id}
                onSuccess={onConvertSuccess}
                onCancel={() => setConvertOpen(false)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LogSection({ title, entries }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="text-xs">
        <div className="font-medium text-gray-700">{title}</div>
        <div className="text-gray-400 italic">No activity yet</div>
      </div>
    );
  }
  return (
    <div className="text-xs">
      <div className="font-medium text-gray-700 mb-1">{title}</div>
      <div className="space-y-1">
        {entries.slice().reverse().map((e, i) => (
          <div key={i} className="bg-gray-50 border rounded p-2">
            <div className="text-gray-500">{fmtDateTime(e.at)}</div>
            <div className="text-gray-700">
              Status: <strong>{e.result?.status || 'unknown'}</strong>
              {e.result?.mock && <span className="ml-2 text-amber-600">(MOCKED)</span>}
              {e.body && <div className="mt-1">📝 {e.body}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== Manual activity list (sales team's own actions) =====
const ACTIVITY_TYPE_META = {
  call: { Icon: Phone, label: 'Call', tint: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  sms: { Icon: MessageSquare, label: 'Text', tint: 'text-sky-700 bg-sky-50 border-sky-200' },
  whatsapp: { Icon: MessageSquare, label: 'WhatsApp', tint: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  email: { Icon: MessageSquare, label: 'Email', tint: 'text-violet-700 bg-violet-50 border-violet-200' },
  meeting: { Icon: MessageSquare, label: 'Meeting', tint: 'text-amber-700 bg-amber-50 border-amber-200' },
  note: { Icon: StickyNote, label: 'Note', tint: 'text-violet-700 bg-violet-50 border-violet-200' },
};

function ManualActivityList({ entries }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="text-xs bg-tge-cream-soft/60 rounded-lg p-3">
        <div className="font-semibold text-tge-navy mb-1">Team activity</div>
        <div className="text-tge-muted italic">No team actions logged yet. Use the quick-action buttons above.</div>
      </div>
    );
  }
  return (
    <div className="text-xs">
      <div className="font-semibold text-tge-navy mb-2">Team activity ({entries.length})</div>
      <div className="space-y-1.5">
        {entries.map((a) => {
          const meta = ACTIVITY_TYPE_META[a.type] || { Icon: StickyNote, label: a.type, tint: 'text-gray-700 bg-gray-50 border-gray-200' };
          return (
            <div key={a.id} className={`border rounded-lg p-2.5 ${meta.tint}`} data-testid={`manual-activity-${a.id}`}>
              <div className="flex items-center gap-2 mb-0.5">
                <meta.Icon size={13} />
                <span className="font-semibold">{meta.label}</span>
                {a.outcome && <span className="bg-white/70 border border-current/20 px-1.5 py-0.5 rounded-full text-[10px] capitalize">{a.outcome.replaceAll('_', ' ')}</span>}
                <span className="ml-auto text-[10px] opacity-70">{fmtDateTime(a.created_at)}</span>
              </div>
              <div className="text-[11px] opacity-80">by <strong>{a.actor_name}</strong> ({a.actor_role})</div>
              {a.note && <div className="mt-1.5 text-[12px] text-tge-navy/90 bg-white/70 rounded p-2">{a.note}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== Inline badges for the table row =====
function ActivityBadges({ counts }) {
  if (!counts) return null;
  const items = [
    { key: 'call', label: 'calls', Icon: Phone, v: counts.call },
    { key: 'sms', label: 'texts', Icon: MessageSquare, v: counts.sms + (counts.whatsapp || 0) },
    { key: 'note', label: 'notes', Icon: StickyNote, v: counts.note },
  ].filter((it) => it.v > 0);
  if (items.length === 0) return <span className="text-[11px] text-tge-muted italic">No team activity</span>;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {items.map((it) => (
        <span key={it.key} className="inline-flex items-center gap-1 bg-tge-cream-soft text-tge-navy text-[10px] px-1.5 py-0.5 rounded-full font-semibold">
          <it.Icon size={10} /> {it.v}
        </span>
      ))}
    </div>
  );
}

// ===== Wide table view =====
function LeadTable({ leads, activityCounts, canSelect, selection, toggleSelect, openDetails, openActivity, openConvert }) {
  if (leads.length === 0) {
    return (
      <div className="bg-white border border-tge-navy/5 rounded-2xl py-16 text-center text-sm text-tge-muted">
        No leads match the current filter.
      </div>
    );
  }
  return (
    <div className="bg-white border border-tge-navy/5 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full" data-testid="leads-table">
          <thead className="bg-tge-cream-soft">
            <tr>
              {canSelect && <th className="w-8 px-3 py-3"></th>}
              <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-tge-muted">Lead</th>
              <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-tge-muted">AI Heat</th>
              <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-tge-muted">Source · Stage</th>
              <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-tge-muted">Interest</th>
              <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-tge-muted">Team activity</th>
              <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider font-semibold text-tge-muted">Quick actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-tge-cream">
            {leads.map((l) => {
              const checked = selection.has(l.id);
              const heat = HEAT_META[l.ai_heat] || { label: '—', color: 'bg-gray-100 text-gray-700 border-gray-200', bar: 'bg-gray-300' };
              const counts = activityCounts[l.id];
              return (
                <tr
                  key={l.id}
                  className={`hover:bg-tge-cream-soft/50 cursor-pointer transition ${checked ? 'bg-tge-gold/5' : ''}`}
                  onClick={() => openDetails(l.id)}
                  data-testid={`lead-row-${l.id}`}
                >
                  {canSelect && (
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => toggleSelect(l.id, e)}
                        className="w-4 h-4 accent-tge-gold"
                        data-testid={`lead-row-check-${l.id}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 min-w-[200px]">
                    <div className="font-semibold text-sm text-tge-navy truncate">{l.name || 'Unnamed lead'}</div>
                    <div className="text-xs text-tge-muted">{l.phone || '—'} {l.email && `· ${l.email}`}</div>
                    <div className="text-[10px] text-tge-muted/70 mt-0.5">{fmtDateTime(l.created_at)}</div>
                  </td>
                  <td className="px-4 py-3 min-w-[140px]">
                    <div className="flex items-center gap-2">
                      <Badge className={`${heat.color} border text-[10px]`}>{heat.label}</Badge>
                      <span className="text-xs font-mono text-tge-muted">{l.ai_score ?? '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-tge-navy capitalize">{l.source}</div>
                    <div className="text-[11px] text-tge-muted capitalize">{l.status}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-tge-navy/80 min-w-[180px]">
                    <div className="truncate">{l.program_interest || '—'}</div>
                    <div className="text-[11px] text-tge-muted truncate">{l.country_interest || ''}</div>
                  </td>
                  <td className="px-4 py-3 min-w-[180px]">
                    <ActivityBadges counts={counts} />
                    {counts?.last_actor && (
                      <div className="text-[10px] text-tge-muted mt-1">Last: {counts.last_actor}</div>
                    )}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-end flex-wrap">
                      {l.status === 'converted' && !l.converted_to_student_id && (
                        <button
                          title="Create student profile from this lead"
                          onClick={() => openConvert(l)}
                          data-testid={`row-action-convert-${l.id}`}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-900 bg-amber-100 border border-amber-300 hover:bg-amber-200 rounded-full px-2.5 py-1 transition"
                        >
                          <UserPlus size={11} /> Convert to student
                        </button>
                      )}
                      {l.converted_to_student_id && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                          ✓ Student created
                        </span>
                      )}
                      <button
                        title="Log a call"
                        onClick={() => openActivity(l.id, l.name, 'call')}
                        data-testid={`row-action-call-${l.id}`}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition"
                      >
                        <Phone size={13} />
                      </button>
                      <button
                        title="Log a text/SMS"
                        onClick={() => openActivity(l.id, l.name, 'sms')}
                        data-testid={`row-action-sms-${l.id}`}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sky-700 bg-sky-50 border border-sky-200 hover:bg-sky-100 transition"
                      >
                        <MessageSquare size={13} />
                      </button>
                      <button
                        title="Add a note"
                        onClick={() => openActivity(l.id, l.name, 'note')}
                        data-testid={`row-action-note-${l.id}`}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100 transition"
                      >
                        <StickyNote size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== Kanban / Board view =====
function LeadBoard({ stages, activityCounts, canSelect, selection, toggleSelect, openDetails, openActivity, openConvert }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
      {PIPELINE_STAGES.map((stage) => (
        <div key={stage.key} className="bg-tge-cream-soft/60 rounded-2xl p-3 min-h-[300px]" data-testid={`lead-column-${stage.key}`}>
          <div className={`text-xs font-semibold px-2.5 py-1 rounded-full border inline-block mb-3 ${stage.color}`}>
            {stage.label} ({stages[stage.key]?.length || 0})
          </div>
          <div className="space-y-2">
            {stages[stage.key]?.map((lead) => {
              const checked = selection.has(lead.id);
              const heat = HEAT_META[lead.ai_heat] || { label: '—', color: 'bg-gray-100 text-gray-700 border-gray-200', bar: 'bg-gray-300' };
              const counts = activityCounts[lead.id];
              return (
                <div
                  key={lead.id}
                  className={`group bg-white rounded-xl border transition p-3 cursor-pointer relative ${checked ? 'border-tge-gold ring-2 ring-tge-gold/30' : 'border-tge-navy/5 hover:border-tge-navy/30 hover:shadow-sm'}`}
                  onClick={() => openDetails(lead.id)}
                  data-testid={`lead-card-${lead.id}`}
                >
                  {canSelect && (
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleSelect(lead.id, e)}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-2 right-2 w-4 h-4 accent-tge-gold cursor-pointer"
                      data-testid={`lead-checkbox-${lead.id}`}
                    />
                  )}
                  <div className="flex items-center justify-between mb-1.5 pr-5">
                    <div className="font-medium text-sm text-tge-navy truncate">{lead.name || 'Unnamed lead'}</div>
                  </div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Badge className={`${heat.color} border text-[9px] px-1.5 py-0`}>{heat.label}</Badge>
                    <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full ${heat.bar}`} style={{ width: `${lead.ai_score || 0}%` }} />
                    </div>
                    <span className="text-[10px] font-mono text-tge-muted w-7 text-right">{lead.ai_score ?? '—'}</span>
                  </div>
                  <div className="text-xs text-tge-muted truncate">{lead.phone || '—'}</div>
                  {lead.program_interest && <div className="text-[11px] text-tge-navy/70 mt-1 truncate">📚 {lead.program_interest}</div>}
                  <div className="mt-2"><ActivityBadges counts={counts} /></div>
                  {lead.status === 'converted' && !lead.converted_to_student_id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); openConvert(lead); }}
                      data-testid={`card-action-convert-${lead.id}`}
                      className="mt-2 w-full inline-flex items-center justify-center gap-1 text-[11px] font-semibold text-amber-900 bg-amber-100 border border-amber-300 hover:bg-amber-200 rounded-full px-2 py-1 transition"
                    >
                      <UserPlus size={11} /> Convert to student
                    </button>
                  )}
                  {lead.converted_to_student_id && (
                    <div className="mt-2 text-center text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                      ✓ Student created
                    </div>
                  )}
                  <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-tge-cream" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openActivity(lead.id, lead.name, 'call')} title="Log call" className="w-6 h-6 rounded-full flex items-center justify-center text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition" data-testid={`card-action-call-${lead.id}`}><Phone size={11} /></button>
                    <button onClick={() => openActivity(lead.id, lead.name, 'sms')} title="Log text" className="w-6 h-6 rounded-full flex items-center justify-center text-sky-700 bg-sky-50 hover:bg-sky-100 transition" data-testid={`card-action-sms-${lead.id}`}><MessageSquare size={11} /></button>
                    <button onClick={() => openActivity(lead.id, lead.name, 'note')} title="Add note" className="w-6 h-6 rounded-full flex items-center justify-center text-violet-700 bg-violet-50 hover:bg-violet-100 transition" data-testid={`card-action-note-${lead.id}`}><StickyNote size={11} /></button>
                  </div>
                </div>
              );
            })}
            {(!stages[stage.key] || stages[stage.key].length === 0) && (
              <div className="text-xs text-tge-muted italic text-center py-6">No leads</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
